import { useState, useCallback, useMemo, useEffect } from 'react'
import {
  getPedidos, savePedido, deletePedido, updateStatus,
  getPedidosBlingAtivos, setBlingStatusOverride, hideBlingPedido,
} from '../utils/storage'
import { clienteExcluido } from '../data/exclusoes'

export function useOrders() {
  const [pedidosLocais, setPedidosLocais] = useState(() => getPedidos())
  const [blingAtivos,   setBlingAtivos]   = useState(() => getPedidosBlingAtivos())

  const refresh = useCallback(() => {
    setPedidosLocais(getPedidos())
    setBlingAtivos(getPedidosBlingAtivos())
  }, [])

  // Quando o useAutoSync re-sincroniza com Supabase em background,
  // ele dispara 'mini:resynced' — a UI precisa re-ler o localStorage
  // pra mostrar os dados novos. Sem isso, o sync acontece mas a tela
  // continua mostrando o estado antigo.
  useEffect(() => {
    function onResynced() { refresh() }
    window.addEventListener('mini:resynced', onResynced)
    return () => window.removeEventListener('mini:resynced', onResynced)
  }, [refresh])

  // combina locais + Bling (locais têm precedência em caso de mesmo número)
  //
  // Antes era dedup puro — descartava o Bling. Mas isso quebrava quando o
  // pedido local foi cadastrado antes do endereço estar completo (ou o
  // endereço completo foi adicionado direto no Bling depois). A Logística
  // perdia cidade/bairro/endereço e o pedido sumia das abas regionais.
  //
  // Agora: faz MERGE — local vence em todo campo onde ele tem dado;
  // onde o local tá vazio, usa o do Bling. Sem sobrescrever nada.
  const pedidos = useMemo(() => {
    const blingPorNumero = new Map(blingAtivos.map(b => [b.numero, b]))

    // Helper: mantém o valor do local se não-vazio; senão usa o do Bling
    const pick = (a, b) => (a !== undefined && a !== null && a !== '') ? a : b

    const locaisEnriquecidos = pedidosLocais.map(local => {
      const bling = blingPorNumero.get(local.numero)
      if (!bling) return local
      const lc = local.cliente || {}
      const bc = bling.cliente || {}
      return {
        ...local,
        cliente: {
          ...lc,
          nome:     pick(lc.nome,     bc.nome),
          contato:  pick(lc.contato,  bc.contato),
          email:    pick(lc.email,    bc.email),
          endereco: pick(lc.endereco, bc.endereco),
          bairro:   pick(lc.bairro,   bc.bairro),
          cidade:   pick(lc.cidade,   bc.cidade),
          cep:      pick(lc.cep,      bc.cep),
          uf:       pick(lc.uf,       bc.uf),
        },
        // Resumo / vendedora também — local vence, Bling completa
        resumo:    pick(local.resumo,    bling.resumo),
        vendedora: pick(local.vendedora, bling.vendedora),
        // Prazo / dataVenda — críticos pra Logística aparecer. Pedido
        // cadastrado local sem prazo (cliente "entregar quando ficar
        // pronto") e prazo definido depois só no Bling NÃO aparecia
        // na Logística porque o filtro exige p.prazo.startsWith('2026').
        prazo:     pick(local.prazo,     bling.prazo),
        dataVenda: pick(local.dataVenda, bling.dataVenda),
        // Valor total — se o local tá zerado, usa o do Bling
        valorTotal: Number(local.valorTotal) || Number(bling.valorTotal) || 0,
        // Mantém link com o Bling pra futuras consultas
        blingId:     local.blingId     || bling.blingId,
        blingNumero: local.blingNumero || bling.blingNumero,
      }
    })

    const numerosLocais = new Set(locaisEnriquecidos.map(p => p.numero))
    const blingFiltrado = blingAtivos.filter(p => !numerosLocais.has(p.numero))
    // Exclui clientes da outra marca (ex: móveis planejados da Arco que
    // vieram parar aqui por erro de canal). Central em useOrders → some
    // de TODAS as telas (Pedidos, Logística, Kanban, Dashboards, RFM).
    return [...locaisEnriquecidos, ...blingFiltrado].filter(p => !clienteExcluido(p))
  }, [pedidosLocais, blingAtivos])

  const salvar = useCallback((pedido) => {
    savePedido(pedido)
    refresh()
    return pedido
  }, [refresh])

  // Salva no localStorage SEM disparar refresh (útil para operações em lote)
  const salvarSilencioso = useCallback((pedido) => {
    savePedido(pedido)
    return pedido
  }, [])

  const remover = useCallback((id) => {
    if (String(id).startsWith('bling-')) {
      hideBlingPedido(id)
    } else {
      deletePedido(id)
    }
    refresh()
  }, [refresh])

  const moverStatus = useCallback((id, status) => {
    if (String(id).startsWith('bling-')) {
      setBlingStatusOverride(id, status)
    } else {
      updateStatus(id, status)
    }
    refresh()
  }, [refresh])

  return { pedidos, salvar, salvarSilencioso, remover, moverStatus, refresh }
}
